import { parsePhoneNumberFromString } from "libphonenumber-js"
import { toast } from "@/components/pop-ui/pop-toast"
import { getFrontendErrorType, handleBusinessError } from "@/lib/error-handler"
import { handleGatewayError } from "@/lib/request-handlers"
import { getT } from "@/providers/translations-provider"

// 页面状态枚举
export type FormState =
	| "check"
	| "login"
	| "register"
	| "reset"
	| "link-email"
	| "link-phone"
	| "change-email"
	| "change-phone"

// 错误类型
export interface UserFormErrors {
	account?: string
	password?: string
	code?: string
	email?: string
	phone?: string
	area?: string
}

export interface AreaCode {
	code: string
	key: string
	phoneCode: string
	digitLength: number[]
}

export interface LoginFormProps {
	inputValue: string
	area: string
	onResetPassword: () => void
}

export interface RegisterFormProps {
	inputValue: string
	area: string
	onFinish?: () => void
}

// 认证错误码 - en-us版本
const errorCodesEnUs = {
	OTP_EXPIRED: "OTP expired",
	INVALID_OTP: "Invalid OTP",
	INVALID_PASSWORD: "Invalid password",
	INVALID_EMAIL: "Invalid email",
	INVALID_TOKEN: "Invalid token",
	USER_NOT_FOUND: "User not found",
	FAILED_TO_CREATE_USER: "Failed to create user",
	FAILED_TO_CREATE_SESSION: "Failed to create session",
	FAILED_TO_UPDATE_USER: "Failed to update user",
	FAILED_TO_GET_SESSION: "Failed to get session",
	INVALID_EMAIL_OR_PASSWORD: "Invalid email or password",
	SOCIAL_ACCOUNT_ALREADY_LINKED: "Social account already linked",
	PROVIDER_NOT_FOUND: "Provider not found",
	ID_TOKEN_NOT_SUPPORTED: "ID token not supported",
	FAILED_TO_GET_USER_INFO: "Failed to get user info",
	USER_EMAIL_NOT_FOUND: "User email not found",
	EMAIL_NOT_VERIFIED: "Email not verified",
	PASSWORD_TOO_SHORT: "Password too short",
	PASSWORD_TOO_LONG: "Password too long",
	USER_ALREADY_EXISTS: "User already exists",
	EMAIL_CAN_NOT_BE_UPDATED: "Email cannot be updated",
	CREDENTIAL_ACCOUNT_NOT_FOUND: "Credential account not found",
	SESSION_EXPIRED: "Session expired. Re-authenticate to perform this action.",
	FAILED_TO_UNLINK_LAST_ACCOUNT: "You can't unlink your last account",
	ACCOUNT_NOT_FOUND: "Account not found",
	BANNED_USER:
		"You have been banned from this application. Please contact support if you believe this is an error.",
}

// 国家/地区列表
export const AREA_LIST: AreaCode[] = [
	{
		code: "HK",
		key: "area.hk",
		phoneCode: "852",
		digitLength: [8],
	},
	{
		code: "MO",
		key: "area.mo",
		phoneCode: "853",
		digitLength: [8, 9, 10, 11, 12, 13, 14, 15],
	},
	{
		code: "TW",
		key: "area.tw",
		phoneCode: "886",
		digitLength: [9],
	},
	{
		code: "GB",
		key: "area.gb",
		phoneCode: "44",
		digitLength: [10, 11, 12],
	},
]

// 判断是邮箱还是手机号
export const isEmail = (value: string) => {
	return value.includes("@")
}

// 验证邮箱格式
export const validateEmail = (email: string) => {
	// 更严格的邮箱格式验证规则
	const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
	return emailRegex.test(email)
}

// 使用与后端相同的 libphonenumber-js 验证手机号
export const validatePhoneNumber = (
	phone: string,
	area?: string, // 可选参数，用于指定期望的地区
): {
	valid: boolean
	regionCode?: string
	normalizedPhone?: string // 标准化后的电话号码（去掉 + 前缀，适用于短信发送）
} => {
	const input = String(phone || "").trim()
	if (!input) return { valid: false }

	// 1) 归一化处理：去除空格与连接符
	let normalized = input.replace(/[\s-]/g, "")

	// 2) 兼容 00 开头国际前缀 => 转为 +
	if (normalized.startsWith("00")) {
		normalized = `+${normalized.slice(2)}`
	}

	// 3) 如果是纯数字且不以 + 开头，根据地区进行智能处理
	if (/^\d+$/.test(normalized) && !normalized.startsWith("+")) {
		// 如果指定了地区，优先使用该地区的国家代码
		if (area) {
			const areaInfo = AREA_LIST.find((item) => item.code === area)
			if (areaInfo) {
				// 检查用户是否已经包含了国家代码
				const possibleCountryCode = normalized.slice(
					0,
					areaInfo.phoneCode.length,
				)
				if (possibleCountryCode === areaInfo.phoneCode) {
					// 用户已经包含了国家代码，直接添加 + 前缀
					normalized = `+${normalized}`
				} else if (normalized.startsWith("0")) {
					// 特殊处理：如果是以0开头的数字，移除0并添加国家代码
					normalized = `+${areaInfo.phoneCode}${normalized.slice(1)}`
				} else {
					// 直接添加国家代码
					normalized = `+${areaInfo.phoneCode}${normalized}`
				}
			}
		} else {
			// 如果没有指定地区，尝试智能识别
			// 特殊处理：如果是以0开头的数字，可能是英国本地号码，需要添加+44
			if (normalized.startsWith("0")) {
				normalized = `+44${normalized.slice(1)}`
			} else {
				// 对于不以0开头的数字，尝试不同的国家代码
				const commonPrefixes = [
					"44",
					"1",
					"86",
					"81",
					"82",
					"852",
					"853",
					"886",
				]
				let found = false
				for (const prefix of commonPrefixes) {
					const testNumber = `+${prefix}${normalized}`
					const testPn = parsePhoneNumberFromString(testNumber)
					if (testPn?.isValid()) {
						normalized = testNumber
						found = true
						break
					}
				}
				// 如果都不匹配，保持原样
				if (!found) {
					normalized = `+${normalized}`
				}
			}
		}
	}

	try {
		const pn = parsePhoneNumberFromString(normalized)
		if (pn?.isValid()) {
			// 返回呼叫区号（不带 +）以兼容历史 regionCode 语义
			// 标准化后的电话号码，去掉 + 前缀，适用于短信发送（如阿里云SMS）
			return {
				valid: true,
				regionCode: pn.countryCallingCode,
				normalizedPhone: pn.number.slice(1), // 去掉 + 前缀
			}
		}

		// Fallback validation for UK numbers that might not be recognized by libphonenumber-js
		if (area === "GB" && normalized.startsWith("+44")) {
			const ukNumber = normalized.slice(3) // Remove +44
			// UK mobile numbers: 7xxx xxxxxx (10 digits starting with 7)
			// UK landline numbers: 1xxx xxxxxx, 2xxx xxxxxx, 3xxx xxxxxx, etc.
			if (/^[1-9]\d{9}$/.test(ukNumber)) {
				return {
					valid: true,
					regionCode: "44",
					normalizedPhone: `44${ukNumber}`,
				}
			}
		}

		// Additional fallback for UK numbers without + prefix
		if (area === "GB" && normalized.startsWith("44")) {
			const ukNumber = normalized.slice(2) // Remove 44
			// UK mobile numbers: 7xxx xxxxxx (10 digits starting with 7)
			// UK landline numbers: 1xxx xxxxxx, 2xxx xxxxxx, 3xxx xxxxxx, etc.
			if (/^[1-9]\d{9}$/.test(ukNumber)) {
				return {
					valid: true,
					regionCode: "44",
					normalizedPhone: `44${ukNumber}`,
				}
			}
		}
	} catch (error) {
		console.error("parsePhoneNumberFromStringError", {
			error,
			phone,
			normalized,
		})
		return { valid: false }
	}

	return { valid: false }
}

// 简化的手机号验证函数，保持向后兼容
export const validatePhone = (
	phone: string | undefined,
	area: string,
): boolean => {
	// 如果 phone 是 undefined 或空字符串，返回 false
	if (!phone || typeof phone !== "string") {
		return false
	}

	const result = validatePhoneNumber(phone, area)
	if (!result.valid) return false

	// 检查区域代码是否匹配
	const areaInfo = AREA_LIST.find((item) => item.code === area)
	if (!areaInfo) return false

	return result.regionCode === areaInfo.phoneCode
}

// 格式化手机号显示
export const formatPhoneDisplay = (
	phone: string | undefined,
	area: string,
): string => {
	// 如果 phone 是 undefined 或空字符串，返回空字符串
	if (!phone || typeof phone !== "string") {
		return ""
	}

	const areaInfo = AREA_LIST.find((item) => item.code === area)
	if (!areaInfo) return phone

	return `+${areaInfo.phoneCode}-${phone}`
}

// 简化的手机号处理函数
export const processPhoneInput = (
	phone: string | undefined,
	area: string,
): string => {
	// 如果 phone 是 undefined 或空字符串，返回空字符串
	if (!phone || typeof phone !== "string") {
		return ""
	}

	// 清理输入，移除空格和其他非数字字符
	const cleanedPhone = phone.replace(/\D/g, "")

	// 使用 libphonenumber-js 进行标准化处理
	const result = validatePhoneNumber(cleanedPhone, area)
	if (result.valid && result.normalizedPhone) {
		// 移除区号部分，只返回本地号码
		const areaInfo = AREA_LIST.find((item) => item.code === area)
		if (areaInfo) {
			const localNumber = result.normalizedPhone.replace(areaInfo.phoneCode, "")
			return localNumber
		}
		return result.normalizedPhone
	}

	// 如果验证失败，返回清理后的输入
	return cleanedPhone
}

// 校验密码工具函数，返回错误信息或 null
export function validatePassword(
	password: string,
	t: (key: string) => string,
): string | null {
	if (!password) {
		return t("error.password.required")
	} else if (password.length < 6) {
		return t("error.password.minLength")
	} else if (password.length > 40) {
		return t("error.password.maxLength")
	}
	return null
}

// 简化的手机号格式化函数
export const formatPhoneParam = (
	phone: string | undefined,
	area: string,
): string => {
	// 如果 phone 是 undefined 或空字符串，返回空字符串
	if (!phone || typeof phone !== "string") {
		return ""
	}

	// 清理输入，移除空格和其他非数字字符
	const cleanedPhone = phone.replace(/\D/g, "")

	// 使用 libphonenumber-js 进行标准化处理
	const result = validatePhoneNumber(cleanedPhone, area)
	if (result.valid && result.normalizedPhone) {
		return result.normalizedPhone
	}

	// 如果验证失败，回退到原来的逻辑
	const areaInfo = AREA_LIST.find((item) => item.code === area)
	const processedPhone = processPhoneInput(phone, area)

	if (!areaInfo) {
		return processedPhone
	}
	return `${areaInfo.phoneCode}${processedPhone}`
}

export function getCfTurnstileToken() {
	return (
		document.querySelector(
			".cf-turnstile input[name='cf-turnstile-response']",
		) as HTMLInputElement
	)?.value
}

/**
 * 通用表單接口錯誤處理方法
 * @param error 接口返回的錯誤對象
 * @param defaultMessageKey 默認錯誤消息的翻譯鍵
 * @returns 錯誤消息字符串，用於表單顯示
 */
export async function handleAuthApiError(
	error: any,
	defaultMessageKey: string = "error.api.default",
): Promise<void> {
	const t = getT()

	if (error._isBusinessErrorToasted) {
		return
	}

	// 检查是否为网关防护错误
	if (error?.status && handleGatewayError(error.status, t)) {
		return
	}

	// 提取錯誤碼，檢查是否為認證相關的特定錯誤
	let errorCode: string | undefined
	let _errorMessage: string | undefined
	let frontendErrorType: string | undefined

	// 格式1: ErrorResponse {json: {code: xxx, message: 'yyy'}}
	if (error?.json && typeof error.json === "object") {
		errorCode = error.json.code
		_errorMessage = error.json.message
		if (errorCode) {
			frontendErrorType = getFrontendErrorType(errorCode)
		}
	}
	// 格式2: StandardErrorData {code: xxx, message: 'yyy'}
	else if (error?.code !== undefined && error?.message) {
		errorCode = error.code
		_errorMessage = error.message
		if (errorCode) {
			frontendErrorType = getFrontendErrorType(errorCode)
		}
	}

	// 優先使用新的錯誤類型映射進行處理
	if (frontendErrorType) {
		// 根據前端錯誤類型進行分類處理
		switch (frontendErrorType) {
			case "banned":
			case "user_blacklisted":
				// 用戶被封禁，顯示特殊錯誤信息
				toast.error(t("BANNED_USER"))
				return
			case "user_not_found":
				// 用戶不存在，可能需要重新登錄
				toast.error(t("USER_NOT_FOUND"))
				return
			case "account_provider_already_linked":
			case "account_provider_already_linked_for_user":
				// 社交帳號已連結
				toast.error(t("SOCIAL_ACCOUNT_ALREADY_LINKED"))
				return
			case "account_already_exists_in_area":
				// 帳戶在該區域已存在
				toast.error(t("USER_ALREADY_EXISTS"))
				return
			case "email_blacklisted":
			case "phone_blacklisted":
				// 郵箱或手機號被列入黑名單
				toast.error(t("BANNED_USER"))
				return
			case "invalid_password":
				// 密碼錯誤
				toast.error(t("INVALID_PASSWORD"))
				return
			case "otp_invalid":
				// 驗證碼錯誤
				toast.error(t("INVALID_OTP"))
				return
			case "email_otp_rate_limited":
				// 驗證碼請求過於頻繁
				toast.error(t("EMAIL_OTP_RATE_LIMITED"))
				return
			case "captcha_verification_failed":
				// CAPTCHA 驗證失敗，使用服務器返回的錯誤消息
				if (_errorMessage) {
					toast.error(_errorMessage)
				} else {
					toast.error(t("error.captchaVerificationFailed.description"))
				}
				return
			case "invalid_email_format":
				// 郵箱格式錯誤
				toast.error(t("INVALID_EMAIL"))
				return
			case "invalid_phone_format":
				// 手機號格式錯誤
				toast.error(t("INVALID_PHONE_FORMAT"))
				return
			case "empty_fields":
			case "empty_email":
			case "empty_area":
				// 必填欄位為空
				toast.error(t("EMPTY_FIELDS"))
				return
			case "sign_up_disabled":
				// 註冊功能停用
				toast.error(t("SIGN_UP_DISABLED"))
				return
			case "password_not_set":
				// 密碼未設定
				toast.error(t("PASSWORD_NOT_SET"))
				return
			case "unauthorized":
				// 未授權
				toast.error(t("UNAUTHORIZED"))
				return
			case "forbidden":
				// 禁止訪問
				toast.error(t("FORBIDDEN"))
				return
			case "bad_request":
				// 請求錯誤
				toast.error(t("BAD_REQUEST"))
				return
			case "internal_server_error":
				// 服務器錯誤
				toast.error(t("INTERNAL_SERVER_ERROR"))
				return
			default:
				// 如果有錯誤碼但沒有特定的處理邏輯，嘗試使用錯誤碼作為翻譯鍵
				if (errorCode) {
					toast.error(t(errorCode))
					return
				}
		}
	}

	// 如果是認證相關的特定錯誤碼（舊格式），使用自定義的錯誤消息
	if (errorCode && errorCode in errorCodesEnUs) {
		toast.error(t(errorCode))
		return
	}

	// 其他錯誤使用統一錯誤處理系統，但捕獲異常避免顯示toast
	try {
		const _passedError = handleBusinessError(error, {
			defaultMessage: t(defaultMessageKey),
			ignoreUnauthorized: false, // 認證相關錯誤需要處理
		})
	} catch (handledError: any) {
		if (!handledError._isBusinessErrorToasted) {
			toast.error(t(defaultMessageKey))
		}
		return
	}
	// 兜底返回默認錯誤消息
	toast.error(t(defaultMessageKey))
	return
}

/**
 * 将邮箱地址转换为加密显示格式
 * @param email 原始邮箱地址
 * @returns 加密后的邮箱地址，如 exa****@example.com
 */
export function maskEmail(email: string): string {
	if (!email) return ""
	const [username, domain] = email.split("@")
	if (!username || !domain) return email

	const maskedUsername =
		username.length <= 3 ? `${username[0]}****` : `${username.slice(0, 3)}****`

	return `${maskedUsername}@${domain}`
}

/**
 * 将手机号码转换为加密显示格式
 * @param phone 原始手机号码
 * @returns 加密后的手机号码，如 +852-****5678
 */
export function maskPhone(phone: string): string {
	if (!phone) return ""

	// 遍历AREA_LIST查找匹配的区号
	const areaInfo = AREA_LIST.find((area) => phone.startsWith(area.phoneCode))
	if (!areaInfo) return phone

	// 移除区号部分
	const number = phone.slice(areaInfo.phoneCode.length)
	const lastFourDigits = number.slice(-4)
	const maskedPart = "*".repeat(number.length - 4)

	return `+${areaInfo.phoneCode}-${maskedPart}${lastFourDigits}`
}
